/**
 * Period-End Service
 *
 * API client for period-end operations: cash flow, GST reconciliation,
 * provision for bad debts, and FX revaluation.
 */

import { get, post, buildQueryString, type ApiResponse } from './api-client';

// ============================================================================
// Types - Cash Flow
// ============================================================================

export interface CashFlowLine {
  accountId: string;
  accountCode: string;
  accountName: string;
  /** Human-readable group label, e.g. "Receipts from customers", "Tax payments". */
  category: string;
  /** Positive = cash inflow, negative = outflow. */
  amount: number;
  currency: string;
}

export interface CashFlowStatement {
  orgId: string;
  startDate: string;
  endDate: string;
  currency: string;
  /** Always "direct" for now. */
  method: string;
  operatingActivities: CashFlowLine[];
  netOperatingCashFlow: number;
  investingActivities: CashFlowLine[];
  netInvestingCashFlow: number;
  financingActivities: CashFlowLine[];
  netFinancingCashFlow: number;
  netCashChange: number;
  generatedAt: string;
}

export interface DailyCashFlowPoint {
  date: string; // YYYY-MM-DD
  inflow: number;
  outflow: number;
  net: number;
}

export interface DailyCashFlowSeries {
  orgId: string;
  startDate: string;
  endDate: string;
  currency: string;
  points: DailyCashFlowPoint[];
}

// ============================================================================
// Types - GST Reconciliation
// ============================================================================

export interface GSTReconciliation {
  orgId: string;
  startDate: string;
  endDate: string;
  currency: string;
  cgstOutput: number;
  sgstOutput: number;
  igstOutput: number;
  totalOutput: number;
  cgstInput: number;
  sgstInput: number;
  igstInput: number;
  totalInput: number;
  igstUtilised: number;
  cgstUtilised: number;
  sgstUtilised: number;
  netIgstPayable: number;
  netCgstPayable: number;
  netSgstPayable: number;
  totalNetPayable: number;
  isRefundable: boolean;
  rateBreakdown?: GSTRateBucket[];
}

// rate is null for ledgers with no rate scoping (consolidated bucket, or
// balances posted before rate-wise ledgers existed).
export interface GSTRateBucket {
  rate: number | null;
  cgstOutput: number;
  sgstOutput: number;
  igstOutput: number;
  cgstInput: number;
  sgstInput: number;
  igstInput: number;
}

export interface GSTSetOffResult {
  orgId: string;
  asOfDate: string;
  totalNetPayable: number;
  journalEntryId: string;
}

// ============================================================================
// Types - Provision for Bad Debts
// ============================================================================

export interface ProvisionResult {
  orgId: string;
  asOfDate: string;
  grossAR: number;
  provisionAmount: number;
  netAR: number;
  journalEntryId?: string;
  incremental: number;
}

// ============================================================================
// Types - FX Revaluation
// ============================================================================

export interface FXRevaluationResult {
  orgId: string;
  asOfDate: string;
  entriesPosted: number;
  journalEntryIds: string[];
}

// ============================================================================
// API Functions - Cash Flow
// ============================================================================

/**
 * Get cash flow statement for a date range.
 */
export async function getCashFlowStatement(
  orgId: string,
  startDate: string,
  endDate: string
): Promise<ApiResponse<CashFlowStatement>> {
  const query = buildQueryString({ start_date: startDate, end_date: endDate });
  return get<CashFlowStatement>(`/organizations/${orgId}/reports/cash-flow${query}`);
}

/**
 * Get per-day cash inflow/outflow/net for a date range.
 * Used by the dashboard 30-day chart.
 */
export async function getDailyCashFlow(
  orgId: string,
  startDate: string,
  endDate: string
): Promise<ApiResponse<DailyCashFlowSeries>> {
  const query = buildQueryString({ start_date: startDate, end_date: endDate });
  return get<DailyCashFlowSeries>(`/organizations/${orgId}/reports/cash-flow/daily${query}`);
}

// ============================================================================
// API Functions - GST Reconciliation
// ============================================================================

/**
 * Get GST input/output reconciliation for a date range.
 */
export async function getGSTReconciliation(
  orgId: string,
  startDate: string,
  endDate: string
): Promise<ApiResponse<GSTReconciliation>> {
  const query = buildQueryString({ start_date: startDate, end_date: endDate });
  return get<GSTReconciliation>(`/organizations/${orgId}/reports/gst-reconciliation${query}`);
}

/**
 * Post a GST set-off journal entry.
 */
export async function postGSTSetOff(
  orgId: string,
  asOfDate: string,
  createdBy: string
): Promise<ApiResponse<GSTSetOffResult>> {
  return post<GSTSetOffResult>(`/organizations/${orgId}/period-end/gst-setoff`, {
    asOfDate,
    createdBy,
  });
}

// ============================================================================
// API Functions - Provision
// ============================================================================

/**
 * Run provision for bad debts.
 */
export async function runProvision(
  orgId: string,
  asOfDate: string,
  createdBy: string
): Promise<ApiResponse<ProvisionResult>> {
  return post<ProvisionResult>(`/organizations/${orgId}/period-end/provision`, {
    asOfDate,
    createdBy,
  });
}

// ============================================================================
// API Functions - FX Revaluation
// ============================================================================

/**
 * Run FX revaluation for open foreign-currency balances.
 */
// eslint-disable-next-line max-params -- TODO(refactor): convert to options object
export async function runFXRevaluation(
  orgId: string,
  asOfDate: string,
  reversalDate: string,
  baseCurrency: string,
  createdBy: string,
  closingRates: Record<string, string>
): Promise<ApiResponse<FXRevaluationResult>> {
  return post<FXRevaluationResult>(`/organizations/${orgId}/period-end/fx-revaluation`, {
    asOfDate,
    reversalDate,
    baseCurrency,
    createdBy,
    closingRates,
  });
}
